// src/theme/colors.js

// Ekran görüntülerine uygun açık mavi/sky blue tema
// Ana renk: #5CB3FF civarı (Light Sky Blue)
const brandPalette = {
  50: '#E8F5FF',   // Çok açık mavi
  100: '#C3E4FF',  // Açık mavi
  200: '#9BD5FF',  // Orta açık mavi
  300: '#73C5FF',  // Parlak mavi
  400: '#5CB3FF',  // Ana mavi (ekran görüntüsündeki buton rengi)
  500: '#4DA8FF',  // ANA MARKA RENGİ (biraz daha koyu)
  600: '#3D97E6',  // Hover için koyu mavi
  700: '#2D86CC',  // Active için daha koyu
  800: '#2075B3',  // Çok koyu mavi
  900: '#146499',  // En koyu mavi
};

// Alpha değerli renkler (RGBA formatında)
// Bu fonksiyon, hex rengi ve alpha değerini alıp rgba string'i döndürür.
const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string' || hex.length < 4) {
    // console.warn(`Invalid hex color: ${hex} for alpha conversion`);
    return `rgba(0,0,0,${alpha})`; // Fallback to black with alpha
  }
  try {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function(m, r, g, b) {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
        // console.warn(`Could not parse hex color: ${hex} for alpha conversion`);
        return `rgba(0,0,0,${alpha})`; // Fallback
    }
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch (error) {
    // console.error(`Error converting hex ${hex} to RGBA:`, error);
    return `rgba(0,0,0,${alpha})`; // Fallback
  }
};

const colors = {
  transparent: "transparent",
  black: "#000000",
  white: "#FFFFFF",
  brand: {
    ...brandPalette,
    // Brand için alpha değerli renkler
    '500-alpha-20': hexToRgba(brandPalette[500], 0.2),
    '500-alpha-25': hexToRgba(brandPalette[500], 0.25),
    '500-alpha-40': hexToRgba(brandPalette[500], 0.4),
    '500_alpha_10': hexToRgba(brandPalette[500], 0.1), // Gradient için
    '300-alpha-30': hexToRgba(brandPalette[300], 0.3),
    '300-alpha-35': hexToRgba(brandPalette[300], 0.35),
    '300-alpha-50': hexToRgba(brandPalette[300], 0.5),
    '800_30Alpha': hexToRgba(brandPalette[800], 0.3),
  },
  gray: { // Chakra'nın varsayılan gray paleti
    50: "#F7FAFC",
    100: "#EDF2F7",
    200: "#E2E8F0",
    300: "#CBD5E0",
    400: "#A0AEC0",
    500: "#718096",
    600: "#4A5568",
    650: "#3A4556", // Elenmiş şıklar için ara ton
    700: "#2D3748",
    750: "#283141", // Ara bir koyu ton
    800: "#1A202C",
    850: "#171A23", // Chat input area için
    900: "#171923",
    '800_60Alpha': hexToRgba('#1A202C', 0.6), // aiCard glassmorphism için
    '800_80Alpha': hexToRgba('#1A202C', 0.8), // headerBg için
    '800_85Alpha': hexToRgba('#1A202C', 0.85),
    '800_90Alpha': hexToRgba('#1A202C', 0.9),
  },
  whiteAlpha: { // Chakra'nın varsayılan whiteAlpha paleti
    50: 'rgba(255, 255, 255, 0.04)',
    100: 'rgba(255, 255, 255, 0.06)',
    200: 'rgba(255, 255, 255, 0.08)',
    300: 'rgba(255, 255, 255, 0.16)',
    400: 'rgba(255, 255, 255, 0.24)',
    500: 'rgba(255, 255, 255, 0.36)',
    600: 'rgba(255, 255, 255, 0.48)',
    700: 'rgba(255, 255, 255, 0.64)',
    800: 'rgba(255, 255, 255, 0.80)',
    850: 'rgba(255, 255, 255, 0.86)',
    900: 'rgba(255, 255, 255, 0.92)',
  },
  blackAlpha: { // Chakra'nın varsayılan blackAlpha paleti
    50: 'rgba(0, 0, 0, 0.04)',
    100: 'rgba(0, 0, 0, 0.06)',
    200: 'rgba(0, 0, 0, 0.08)',
    300: 'rgba(0, 0, 0, 0.16)',
    400: 'rgba(0, 0, 0, 0.24)',
    500: 'rgba(0, 0, 0, 0.36)',
    600: 'rgba(0, 0, 0, 0.48)',
    700: 'rgba(0, 0, 0, 0.64)',
    800: 'rgba(0, 0, 0, 0.80)',
    900: 'rgba(0, 0, 0, 0.92)',
  },
  red: { // Chakra'nın varsayılan red paleti
    50: '#FFF5F5',
    100: '#FED7D7',
    200: '#FEB2B2',
    300: '#FC8181',
    400: '#F56565',
    500: '#E53E3E',
    600: '#C53030',
    700: '#9B2C2C',
    800: '#822727',
    900: '#63171B',
    '500-alpha-40': hexToRgba('#E53E3E', 0.4),
    '300-alpha-50': hexToRgba('#FC8181', 0.5),
    '50_alpha_12': hexToRgba('#FFF5F5', 0.12), // menuItemBackgroundError _dark için rgba(229, 62, 62, 0.12) -> red.500 e yakın
    '500_alpha_12': hexToRgba('#E53E3E', 0.12), // alternatif
  },
  green: { // Chakra'nın varsayılan green paleti
    50: '#F0FFF4',
    100: '#C6F6D5',
    200: '#9AE6B4',
    300: '#68D391',
    400: '#48BB78',
    500: '#38A169',
    600: '#2F855A',
    700: '#276749',
    800: '#22543D',
    900: '#1C4532',
    '400_alpha_10': hexToRgba('#48BB78', 0.1), // Gradient için
  },
  yellow: { // Chakra'nın varsayılan yellow paleti
    50: '#FFFFF0',
    100: '#FEFCBF',
    200: '#FAF089',
    300: '#F6E05E',
    400: '#ECC94B',
    500: '#D69E2E',
    600: '#B7791F',
    700: '#975A16',
    800: '#744210',
    900: '#5F370E',
    '400_alpha_10': hexToRgba('#ECC94B', 0.1), // Gradient için
  },
  blue: { // Chakra'nın varsayılan blue paleti (statusInfo ve explanation için)
    50: '#EBF8FF',
    100: '#BEE3F8',
    200: '#90CDF4',
    300: '#63B3ED',
    400: '#4299E1',
    500: '#3182CE',
    600: '#2B6CB0',
    700: '#2C5282',
    800: '#2A4365',
    900: '#1A365D',
    '500_10Alpha': hexToRgba('#3182CE', 0.1), // notificationItemUnreadBg ve bgExplanation _dark için
    '500_15Alpha': hexToRgba('#3182CE', 0.15),
  },
};

export default colors;
